import { use } from 'chai';
import { stat } from 'fs';
import { set } from 'lodash';
import {
  ComponentProps,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useApi } from 'shared_components/context';
import { useClientId } from 'shared_components/context/client';
import { useLocalStorageState } from 'shared_components/hooks/useLocalStorageState';

export type SourceId = {
  id: string;
  original_name: string;
  valid: boolean;
  rejected: boolean;
  approved: boolean;
  created_by_system: boolean;
};

export type DiscographyId = {
  id: string;
  stageName: string;
  type: string;
  source_ids: {
    deezer: SourceId[];
    discogs: SourceId[];
    musicbrainz: SourceId[];
    spotify: SourceId[];
  };
  manualStageName: boolean;
  is_permanent_member?: boolean;
  is_main_performer?: boolean;
  linked_alias_id?: string | null;
  created_by_system: boolean;
};

export type DiscographySourceType = keyof DiscographyId['source_ids'];

type DiscographyIdsContexType = {
  status: 'not-loaded' | 'loaded' | 'loading' | 'preparing';
  source: string;
  sourceId: string;
  autoCollectIds: (
    clientId: string,
    sourceId: string,
    source: string
  ) => Promise<void>;
  cancelAutoCollectIds: (clientId: string) => Promise<void>;
  cancelAutoCollectIdsInProgress: boolean;
  collectedDiscographyIds: DiscographyId[];
  getCollectedDiscographyIds: (clietId: string) => void;
};

const DiscographyIdsContex = createContext<
  DiscographyIdsContexType | undefined
>(undefined);

const DiscographyIdsProviderImplementation = ({
  children,
  clientId,
}: {
  readonly children: React.ReactNode;
  readonly clientId: string;
}) => {
  console.log('rerendering provider');
  const [status, setStatus] = useLocalStorageState<
    DiscographyIdsContexType['status']
  >('discogs_job_status', 'not-loaded', {
    namespace: clientId,
  });
  const [source, setSource] = useLocalStorageState<
    DiscographyIdsContexType['source']
  >('source', '', {
    namespace: clientId,
  });
  const [sourceId, setSourceId] = useLocalStorageState<
    DiscographyIdsContexType['sourceId']
  >('sourceId', '', {
    namespace: clientId,
  });
  const [collectedDiscographyIds, setCollectedDiscographyIds] = useState<
    DiscographyIdsContexType['collectedDiscographyIds']
  >([]);
  const [cancelAutoCollectIdsInProgress, setCancelAutoCollectIdsInProgress] =
    useState(false);
  const { adminApi: api } = useApi();

  const getCollectedDiscographyIds = useCallback((clientId: string) => {
    api
      .retrieveCollectedIdsAutoCollectedIds({
        userId: clientId,
      })
      .then((data) => {
        console.log('retrieved ids', data);
        const list = data.collectedIds as unknown as any[];
        setCollectedDiscographyIds(
          list.map((item) => ({
            ...item,
            source_ids: {
              discogs: [],
              deezer: [],
              musicbrainz: [],
              spotify: [],
              ...item.source_ids,
            },
          }))
        );
      })
      .catch((err) => {
        console.log(err);
      });
  }, []);

  useEffect(() => {
    if (status !== 'loading') {
      console.log('rerunning get collected ids effect');
      getCollectedDiscographyIds(clientId);
    }
  }, [sourceId, source, clientId, status]);

  useEffect(() => {
    console.log('rerunning job status fetch effect');
    if (status !== 'loading' && status !== 'preparing') return;

    let finalJobStatusFetchTimeout: ReturnType<typeof setTimeout> | null = null;

    const fetchFinalJobStatus = () => {
      if (status !== 'loading' && status !== 'preparing') return;

      api
        .retrieveAutoCollectIdsJobRunStatusPipelineRunStatus({
          userId: clientId,
        })
        .then((data) => {
          switch (data.status as string) {
            case 'QUEUED':
            case 'NOT_STARTED':
            case 'MANAGED':
            case 'STARTING': {
              setStatus('preparing');
              break;
            }
            case 'STARTED': {
              setStatus('loading');
              break;
            }
            case 'SUCCESS': {
              setStatus('loaded');
              break;
            }
            case 'FAILED':
            case 'UNKNOWN':
            default: {
              setStatus('not-loaded');
              break;
            }
          }
        })
        .catch((err) => {
          console.log(err);
          setStatus('not-loaded');
        });

      finalJobStatusFetchTimeout = setTimeout(() => {
        fetchFinalJobStatus();
      }, 3000);
    };

    finalJobStatusFetchTimeout = setTimeout(() => {
      fetchFinalJobStatus();
    }, 3000);

    return () => {
      if (finalJobStatusFetchTimeout) {
        clearTimeout(finalJobStatusFetchTimeout);
      }
    };
  }, [status, clientId]);

  useEffect(() => {
    console.log('running job status fetch effect');

    api
      .retrieveAutoCollectIdsJobRunStatusPipelineRunStatus({ userId: clientId })
      .then((data) => {
        switch (data.status as string) {
          case 'QUEUED':
          case 'NOT_STARTED':
          case 'MANAGED':
          case 'STARTING': {
            setStatus('preparing');
            break;
          }
          case 'STARTED': {
            setStatus('loading');
            break;
          }
          case 'SUCCESS': {
            setStatus('loaded');
            break;
          }
          case 'FAILED':
          case 'UNKNOWN':
          default: {
            setStatus('not-loaded');
            break;
          }
        }
      })
      .catch((err) => {
        console.log(err);
        setStatus((state) =>
          state !== 'loading' && state !== 'preparing' ? 'not-loaded' : state
        );
      });
  }, [clientId]);

  const autoCollectIds = useCallback(
    async (clientId: string, sourceId: string, source: string) => {
      await api
        .autoCollectIdsPipelineRun({
          userId: clientId,
          sourceId,
          source,
        })
        .then(() => {
          setSource(source);
          setSourceId(sourceId);
          setStatus('preparing');
        })
        .catch((err) => {
          console.log(err);
        });
    },
    []
  );

  const cancelAutoCollectIds = useCallback(async () => {
    setCancelAutoCollectIdsInProgress(true);
    setStatus('not-loaded');

    api
      .cancelAutoCollectIdsRemove({
        userId: clientId,
      })
      .catch((ex) => {
        console.log(ex);
      })
      .finally(() => {
        setCancelAutoCollectIdsInProgress(false);
      });
  }, []);

  const value = useMemo(() => {
    console.log('rerendering value', {
      status,
      source,
      sourceId,
      collectedDiscographyIds: collectedDiscographyIds.length,
      autoCollectIds,
      cancelAutoCollectIds,
    });
    return {
      status,
      source,
      sourceId,
      collectedDiscographyIds: collectedDiscographyIds ?? [],
      autoCollectIds,
      cancelAutoCollectIds,
      cancelAutoCollectIdsInProgress,
      getCollectedDiscographyIds,
    };
  }, [status, source, sourceId, collectedDiscographyIds, autoCollectIds]);

  return (
    <DiscographyIdsContex.Provider value={value}>
      {children}
    </DiscographyIdsContex.Provider>
  );
};

export const DiscographyIdsProvider = (
  props: Omit<
    ComponentProps<typeof DiscographyIdsProviderImplementation>,
    'clientId'
  >
) => {
  const { clientId } = useClientId();
  if (!clientId) return null;

  return (
    <DiscographyIdsProviderImplementation {...props} clientId={clientId} />
  );
};

export const useDiscographyIds = () => {
  const context = useContext(DiscographyIdsContex);

  if (!context) throw new Error('Cannot use DiscographyIds without provider');

  return context;
};
